import * as R from 'ramda';
import { _Boolean } from '@/API';
import {
  ListTaskMetadataQueryVariableType,
  ListTaskMetadataQueryType,
  TaskMetadataItem,
  TaskMetadataQueryType,
  TaskMetadataQueryVariableType,
  getTaskMetadatasByVersion,
  listTaskMetadatasWithDetails,
} from '@/appsync/graphql/taskMetadata';
import { graphqlQuery } from '@/appsync/service/GraphqlUtils';
import {
  TaskMetadataArgItemForFront,
  TaskMetadataItemForFront,
} from '@/types/type';
import {
  ifNilThenDefaultTo,
  _BooleanEnum2boolean,
  returnFalseIfNilElseProcess,
  isNumeric,
} from '@/utils/CommonUtils';
import { sortTaskMetadataListArgsByOrder } from '@/utils/TaskUtils';

export const fetchTaskMetadatas = async (
  version: string
): Promise<TaskMetadataItemForFront[]> => {
  const taskMetadataList: TaskMetadataItemForFront[] = [];
  let nextToken: string | undefined | null = null;
  let isFirstPage = true;

  while (isFirstPage || nextToken) {
    isFirstPage = false;
    const variables: TaskMetadataQueryVariableType = {
      version,
      nextToken,
    };
    const result = await graphqlQuery<
      TaskMetadataQueryType,
      TaskMetadataQueryVariableType
    >(getTaskMetadatasByVersion, variables);

    const fetchedTaskMetaData: TaskMetadataItem[] | undefined = R.path(
      ['taskMetadatasByVersion', 'items'],
      result
    );
    if (fetchedTaskMetaData) {
      taskMetadataList.push(...formatTaskMetadataForFront(fetchedTaskMetaData));
    }

    nextToken = R.path(['taskMetadatasByVersion', 'nextToken'], result);
  }
  const taskMetadataListWithArgsSorted = sortTaskMetadataListArgsByOrder(
    taskMetadataList
  );

  const taskMetadataListSortedByBatchName = sortTaskMetadatas(
    taskMetadataListWithArgsSorted
  );
  return taskMetadataListSortedByBatchName;
};

export const fetchLatestTaskMetadatas = async (
  version: string
): Promise<TaskMetadataItemForFront[]> => {
  const taskMetadataList: TaskMetadataItemForFront[] = [];
  let nextToken: string | undefined | null = null;
  let isFirstPage = true;

  while (isFirstPage || nextToken) {
    isFirstPage = false;
    const variables: ListTaskMetadataQueryVariableType = {
      nextToken,
    };
    const result = await graphqlQuery<
      ListTaskMetadataQueryType,
      ListTaskMetadataQueryVariableType
    >(listTaskMetadatasWithDetails, variables);

    const fetchedTaskMetaData: TaskMetadataItem[] | undefined = R.path(
      ['listTaskMetadatas', 'items'],
      result
    );
    if (fetchedTaskMetaData) {
      taskMetadataList.push(...formatTaskMetadataForFront(fetchedTaskMetaData));
    }

    nextToken = R.path(['listTaskMetadatas', 'nextToken'], result);
  }

  const latestVersionTaskMetadata = getLatestVersionTaskMetadata(
    taskMetadataList,
    version
  );

  const taskMetadataListWithArgsSorted = sortTaskMetadataListArgsByOrder(
    latestVersionTaskMetadata
  );

  const taskMetadataListSortedByBatchName = sortTaskMetadatas(
    taskMetadataListWithArgsSorted
  );
  return taskMetadataListSortedByBatchName;
};

// Equivalent to:
// const getLatestVersionTaskMetadata = R.pipe(
//   R.filter(taskMetadata => taskMetadata.version <= version)
//   R.groupBy(R.prop('batch')),
//   R.map(R.pipe(R.sortBy(R.prop('version')), R.last)),
//   R.values
// );
const getLatestVersionTaskMetadata = (
  list: TaskMetadataItemForFront[],
  version: string
): TaskMetadataItemForFront[] => {
  const filterForRemovingFutureVersionData = (
    taskMetadata: TaskMetadataItemForFront
  ) =>
    isNumeric(taskMetadata.version) &&
    Number(taskMetadata.version) <= Number(version);

  const filteredTaskMetadataItemForFront = R.filter(
    filterForRemovingFutureVersionData
  )(list);

  const taskMetadataGroupedByBatch: any = R.groupBy<TaskMetadataItemForFront>(
    R.path<any>(['batch'])
  )(filteredTaskMetadataItemForFront);

  const groupedTaskMetadataSortedByVersion = R.map(
    R.sortBy(
      R.pipe(
        R.path<any>(['version']),
        parseInt
      )
    )
  )(taskMetadataGroupedByBatch);

  const latestTaskMetadataSortedForEachBatch = R.map(R.last)(
    groupedTaskMetadataSortedByVersion
  );

  return R.values(
    latestTaskMetadataSortedForEachBatch
  ) as TaskMetadataItemForFront[];
};

export const sortTaskMetadatas: (
  xs: TaskMetadataItemForFront[]
) => TaskMetadataItemForFront[] = R.sortBy(R.prop('batchName'));

const formatTaskMetadataForFront = (
  taskMetadataList: TaskMetadataItem[]
): TaskMetadataItemForFront[] => {
  const taskMetaDataListForFront: TaskMetadataItemForFront[] = [];

  taskMetadataList.forEach(taskMetadata => {
    const taskMetadataArgsForFront: TaskMetadataArgItemForFront[] = [];

    taskMetadata.args.forEach(taskMetadataArgs => {
      const taskMetadataArgItemForFront: TaskMetadataArgItemForFront = {
        default: taskMetadataArgs.default,
        delimiter: taskMetadataArgs.delimiter,
        display: _BooleanEnum2boolean(taskMetadataArgs.display),
        hints: taskMetadataArgs.hints,
        isInputFile: returnFalseIfNilElseProcess(_BooleanEnum2boolean)(
          taskMetadataArgs.isInputFile
        ),
        isMultiple: returnFalseIfNilElseProcess(_BooleanEnum2boolean)(
          taskMetadataArgs.isMultiple
        ),
        isOutputFile: returnFalseIfNilElseProcess(_BooleanEnum2boolean)(
          taskMetadataArgs.isOutputFile
        ),
        isSecret: returnFalseIfNilElseProcess(_BooleanEnum2boolean)(
          taskMetadataArgs.isSecret
        ),
        isResolvable: returnFalseIfNilElseProcess(_BooleanEnum2boolean)(
          taskMetadataArgs.isResolvable
        ),
        showDownloadToggle: returnFalseIfNilElseProcess(_BooleanEnum2boolean)(
          taskMetadataArgs.isOutputFile
        ),
        labels: taskMetadataArgs.labels,
        option: taskMetadataArgs.option,
        order: taskMetadataArgs.order,
        required: _BooleanEnum2boolean(taskMetadataArgs.required),
        sampleInput: taskMetadataArgs.sampleInput,
        type: taskMetadataArgs.type,
        value: ifNilThenDefaultTo(taskMetadataArgs.default, ''),
      };

      taskMetadataArgsForFront.push(taskMetadataArgItemForFront);
    });

    const taskMetadataItemForFront: TaskMetadataItemForFront = {
      args: taskMetadataArgsForFront,
      batch: taskMetadata.batch,
      batchName: taskMetadata.batchName,
      product: taskMetadata.product,
      version: taskMetadata.version,
      versionProductBatch: taskMetadata.versionProductBatch,
    };

    taskMetaDataListForFront.push(taskMetadataItemForFront);
  });
  return taskMetaDataListForFront;
};
